import { useStyletron } from '@thanx/uikit/theme'
import type { MenuLocationStub } from 'api/menuErrors'
import ModalContainer from 'components/ModalContainer'
import Pagination from 'components/Pagination'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'

const t = buildTranslate('pos_configurations.locations_modal')

// NBC-class merchants ship ~800 locations on a single menu — rendering
// 800 list items + their 1600 line-paragraphs blows up Styletron's
// className graph and slows the modal open by 600ms+ on a low-end laptop.
// 25/page matches the /locations table convention so the operator's
// muscle memory carries over.
const PAGE_SIZE = 25

type Props = {
  isOpen: boolean
  onClose: () => void
  menuName: string
  locations: MenuLocationStub[]
  // The locations come from the lazy-loaded detail query, which only
  // fires when the card expands or this modal opens. While it's in
  // flight we show a loading state rather than a misleading "0 locations".
  isLoading?: boolean
  // When that detail query fails, show a load-error message instead of the
  // empty-state "no locations" copy — otherwise a failed fetch silently
  // reads as "this menu has no locations".
  isError?: boolean
}

// Compose the bold first-line label from the city/state pair, falling
// back to the location name when the BE response omits address fields.
// Keeps the row layout consistent whether or not the optional address
// data is populated.
const primaryLine = (location: MenuLocationStub): string => {
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`
  }
  return location.name
}

const secondaryLine = (location: MenuLocationStub): string | null => {
  if (!location.street) return null
  const parts = [
    location.street,
    location.city,
    [location.state, location.zip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
  return parts.length > 0 ? parts : null
}

// Lists every location associated with the parent menu. Opens from the
// card-header "View locations" link. Matches Figma 432:5945 — a static
// info modal (no per-row links, no chevrons): header with title + count
// subtitle, a "Location" column header, scrollable rows of city/state +
// street address separated by horizontal dividers, and a single Close
// CTA in the footer.
const LocationsModal = ({
  isOpen,
  onClose,
  menuName,
  locations,
  isLoading = false,
  isError = false,
}: Props) => {
  const [css] = useStyletron()
  const [currentPage, setCurrentPage] = useState(1)
  // Subtitle mirrors the body state: loading → "Loading locations…", error →
  // a load-failure message (never a misleading "0 locations"), otherwise the
  // resolved count.
  const subtitle = (
    isLoading
      ? t('loading')
      : isError
      ? t('error')
      : t('subtitle', { count: locations.length })
  ) as string

  const totalPages = Math.max(1, Math.ceil(locations.length / PAGE_SIZE))
  const pageStart = (currentPage - 1) * PAGE_SIZE
  const visibleLocations = useMemo(
    () => locations.slice(pageStart, pageStart + PAGE_SIZE),
    [locations, pageStart]
  )

  // Reset to page 1 every time the modal opens so reopening on a
  // different menu (or after closing partway through) lands on the top
  // of the list instead of a stale page that may not exist for the new
  // dataset. Also guards against the locations array shrinking under
  // currentPage (e.g. a refetch lowering the count).
  useEffect(() => {
    if (isOpen) setCurrentPage(1)
  }, [isOpen, menuName])
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      onProceed={onClose}
      proceedText={t('close') as string}
      hideCancel
      showBottomProceed
      // Float above the OC layout's `main { z-index: 700 }` and the
      // top-nav dropdowns at z-2000, matching LocationDetailsModal so
      // both modals stack correctly when both could theoretically be
      // open at once.
      zIndex={3000}
      ariaLabel={t('aria_label', { menuName }) as string}
    >
      <div
        className={`pb-l mb-l ${css({
          borderBottom: `1px solid ${palette.grey20}`,
        })}`}
      >
        <h3
          className={`mt-0 mb-xxs ${css({
            color: palette.grey90,
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 700,
          })}`}
        >
          {t('title') as string}
        </h3>
        <p
          role={isError ? 'alert' : 'status'}
          className={`body-text-3 mb-0 ${css({ color: palette.grey70 })}`}
        >
          {subtitle}
        </p>
      </div>
      {isLoading || isError ? null : locations.length === 0 ? (
        <p className={`body-text-3 mb-0 ${css({ color: palette.grey70 })}`}>
          {t('empty') as string}
        </p>
      ) : (
        <div>
          <p
            className={`bold mb-m ${css({
              color: palette.grey90,
              fontSize: '14px',
              lineHeight: '20px',
            })}`}
          >
            {t('column_location') as string}
          </p>
          <ul className={`list-unstyled mb-0 ${css({ margin: 0 })}`}>
            {visibleLocations.map((location, idx) => {
              const secondary = secondaryLine(location)
              return (
                <li
                  key={location.id}
                  className={`py-m ${css({
                    borderTop:
                      idx === 0 ? 'none' : `1px solid ${palette.grey20}`,
                  })}`}
                >
                  <p
                    className={`bold mb-xxs ${css({
                      color: palette.grey90,
                      fontSize: '16px',
                      lineHeight: '24px',
                    })}`}
                  >
                    {primaryLine(location)}
                  </p>
                  {secondary && (
                    <p
                      className={`body-text-3 mb-0 ${css({
                        color: palette.grey70,
                      })}`}
                    >
                      {secondary}
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
          {totalPages > 1 && (
            <div className="mt-l">
              {/* Polite SR announcement of the visible slice so paging
                  isn't a silent DOM swap for screen-reader users. */}
              <p className="sr-only" role="status">
                {
                  t('pagination_status', {
                    start: pageStart + 1,
                    end: pageStart + visibleLocations.length,
                    total: locations.length,
                  }) as string
                }
              </p>
              <Pagination
                currentPage={currentPage}
                numPages={totalPages}
                onChangePage={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </ModalContainer>
  )
}

export default LocationsModal
